const API_BASE = "/admin_api";

async function apiRequest(path, options = {}) {
  const response = await fetch(`${API_BASE}/${path}`, {
    ...options,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    throw new Error(`Server returned HTTP ${response.status}.`);
  }

  if (!response.ok || data?.success === false) {
    const error = new Error(data?.message || `Request failed with HTTP ${response.status}.`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function login(email, password) {
  return apiRequest("login.php", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function getSession() {
  return apiRequest("session.php", { method: "GET" });
}

export function forgotPassword(email) {
  return apiRequest("forgot_password.php", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function resetPassword(token, newPassword, confirmPassword) {
  return apiRequest("reset_password.php", {
    method: "POST",
    body: JSON.stringify({
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
  });
}

export function changePassword(currentPassword, newPassword, confirmPassword, csrfToken) {
  return apiRequest("change_password.php", {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken || "",
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
  });
}

export async function logout(csrfToken) {
  return apiRequest("logout.php", {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken || "",
    },
  });
}

export function hasPermission(user, permission) {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
}

function newsletterQuery(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      String(value) !== ""
    ) {
      search.set(key, String(value));
    }
  });

  const query = search.toString();

  return query ? `?${query}` : "";
}

async function newsletterRequest(path, options = {}) {
  const hasBody = options.body !== undefined && options.body !== null;

  return apiRequest(`newsletter/${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });
}

export function newsletterListCampaigns({
  page = 1,
  limit = 20,
  status = "",
  search = "",
} = {}) {
  return newsletterRequest(
    `campaigns.php${newsletterQuery({
      page,
      limit,
      status,
      search,
    })}`,
    {
      method: "GET",
    }
  );
}

export function newsletterCreateCampaign(payload, csrfToken) {
  return newsletterRequest("campaigns.php", {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken || "",
    },
    body: JSON.stringify(payload),
  });
}

export function newsletterGetCampaign(id) {
  return newsletterRequest(
    `campaign.php${newsletterQuery({ id })}`,
    {
      method: "GET",
    }
  );
}

export function newsletterUpdateCampaign(
  id,
  payload,
  csrfToken
) {
  return newsletterRequest(
    `campaign.php${newsletterQuery({ id })}`,
    {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken || "",
      },
      body: JSON.stringify(payload),
    }
  );
}

export function newsletterCancelCampaign(id, csrfToken) {
  return newsletterRequest(
    `campaign.php${newsletterQuery({ id })}`,
    {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken || "",
      },
    }
  );
}

export function newsletterPreviewCampaign(id) {
  return newsletterRequest(
    `preview.php${newsletterQuery({ id })}`,
    {
      method: "GET",
    }
  );
}

export function newsletterTestCampaign(
  id,
  email,
  csrfToken
) {
  return newsletterRequest("test.php", {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken || "",
    },
    body: JSON.stringify({
      campaign_id: Number(id),
      email,
    }),
  });
}

export function newsletterSendCampaign(id, csrfToken) {
  return newsletterRequest("send.php", {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken || "",
    },
    body: JSON.stringify({
      campaign_id: Number(id),
    }),
  });
}

export function newsletterScheduleCampaign(id, scheduledAt, csrfToken) {
  return newsletterRequest(`schedule.php${newsletterQuery({ id })}`, {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken || "",
    },
    body: JSON.stringify({
      scheduled_at: scheduledAt,
    }),
  });
}

export function newsletterListSubscribers({
  page = 1,
  limit = 25,
  status = "active",
  search = "",
} = {}) {
  return newsletterRequest(
    `recipients.php${newsletterQuery({
      page,
      limit,
      status,
      search,
    })}`,
    {
      method: "GET",
    }
  );
}

export function newsletterGetHistory(
  id,
  {
    page = 1,
    limit = 50,
  } = {}
) {
  return newsletterRequest(
    `history.php${newsletterQuery({
      id,
      page,
      limit,
    })}`,
    {
      method: "GET",
    }
  );
}